// Launch animation: Cal pops in and waves, the wordmark arrives, then the app.
//
// This covers the gap the app needs anyway — WebView boot, React mount, the
// first render off the Today cache. The native LaunchScreen.storyboard shows
// the same black behind it, so the handover is invisible rather than a flash.
//
// Motion here is doing a job (§6.1: animation must communicate something).
// It says "we're starting", and it ends the moment there's something to show
// — it never holds the app back longer than it was going to take anyway.
import { useEffect, useState } from "react";
import { Mascot } from "./Mascot";

const REDUCED = typeof window !== "undefined"
  && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Draw → wordmark → hold → fade. Reduced motion skips straight to the held
// state: the brand still appears, it just doesn't perform.
const DRAW_MS = REDUCED ? 0 : 850;
const HOLD_MS = REDUCED ? 350 : 620;
const FADE_MS = 340;

export function Splash() {
  const [phase, setPhase] = useState<"drawing" | "leaving" | "gone">("drawing");

  useEffect(() => {
    const leave = setTimeout(() => setPhase("leaving"), DRAW_MS + HOLD_MS);
    const gone = setTimeout(() => setPhase("gone"), DRAW_MS + HOLD_MS + FADE_MS);
    return () => { clearTimeout(leave); clearTimeout(gone); };
  }, []);

  if (phase === "gone") return null;

  return (
    <div
      // aria-hidden: this is a loading curtain, not content. Screen-reader
      // users land on the app itself, which is already mounted behind it.
      aria-hidden="true"
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-5 bg-canvas"
      style={{
        opacity: phase === "leaving" ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease-out`,
        pointerEvents: phase === "leaving" ? "none" : "auto",
      }}
    >
      <div
        style={{
          animation: REDUCED ? "none" : `splash-pop ${DRAW_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
          ...(REDUCED ? { opacity: 1, transform: "none" } : { opacity: 0 }),
        }}
      >
        <Mascot pose="greeting" size={112} />
      </div>

      <span
        className="text-title font-semibold tracking-tight"
        style={{
          opacity: 0,
          animation: `splash-word 420ms ease-out ${REDUCED ? 0 : DRAW_MS - 180}ms forwards`,
        }}
      >
        caloly
      </span>
    </div>
  );
}
